import moment from 'moment';
import { httpGet, removeTrailingTags } from '../../../utils';
import FileSaver from 'file-saver';
import { satisfies, validate } from 'compare-versions';
import { BaseComponent, SoftwareStatus } from '../types';

export const initSelectedStep = ({
    softwareStatus,
    serversData,
    isOfflineMode,
    steps,
    setSelectedStep,
    WizardStepsMap
}) => {
    const isDownloadStep =
        softwareStatus.downloading ||
        (softwareStatus.versionUpgradeInProgress
            ? softwareStatus.lastDownloadedVersion !== softwareStatus.versionUpgradeInProgress
            : softwareStatus.lastDownloadedVersion !== softwareStatus.maxTowerVersion);

    const isAdminConsoleStep =
        softwareStatus.upgradingTower ||
        softwareStatus.towerUpgradeError ||
        (softwareStatus.versionUpgradeInProgress
            ? softwareStatus.currentTowerVersion !== softwareStatus.versionUpgradeInProgress
            : softwareStatus.currentTowerVersion !== softwareStatus.maxTowerVersion);

    if (!isOfflineMode && isDownloadStep) {
        setSelectedStep(
            Math.max(
                steps.findIndex((step) => step.id === WizardStepsMap.download.id),
                0
            )
        );
    } else if (!isOfflineMode && isAdminConsoleStep) {
        setSelectedStep(
            Math.max(
                steps.findIndex((step) => step.id === WizardStepsMap.adminConsole.id),
                0
            )
        );
    } else if (
        softwareStatus.upgradingPushServices ||
        serversData.pushServices.find((ps) => ps.version !== softwareStatus.currentTowerVersion)
    ) {
        setSelectedStep(
            Math.max(
                steps.findIndex((step) => step.id === WizardStepsMap.pushServices.id),
                0
            )
        );
    } else if (
        softwareStatus.upgradingNodes ||
        serversData.nodes.find((n) => n.version !== softwareStatus.currentTowerVersion)
    ) {
        setSelectedStep(
            Math.max(
                steps.findIndex((step) => step.id === WizardStepsMap.nodes.id),
                0
            )
        );
    } else {
        setSelectedStep(
            Math.max(
                steps.findIndex((step) => step.id === WizardStepsMap.dcs.id),
                0
            )
        );
    }
};

export const handleSendLogs = async ({ setLogsError, setShowSnackbar }) => {
    try {
        setLogsError(false);
        console.log('sending all logs');
        setShowSnackbar(true);
        await httpGet('/Settings/SendAllLogs');
        setTimeout(() => {
            setShowSnackbar(false);
        }, 30000);
    } catch (error) {
        console.error('Error sending logs:', error);
        setLogsError(true);
    }
};

export const handleDownloadLogs = ({ softwareStatus }) => {
    try {
        // Check if logsData exists and is an array
        if (!softwareStatus.logsData || !Array.isArray(softwareStatus.logsData)) {
            console.warn('No logs data available for download');
            return;
        }

        // Join the logs array into a single string with newlines
        const logsContent = softwareStatus.logsData.join('\n');

        // Create a blob with the logs content
        const blob = new Blob([logsContent], { type: 'text/plain;charset=utf-8' });

        // Generate filename with timestamp
        const timestamp = moment().format('DD-MM-YYYY_HH-mm-ss');
        const filename = `SilverfortUpgradeLogs_${timestamp}.txt`;

        // Save the file
        FileSaver.saveAs(blob, filename);
    } catch (error) {
        console.error('Error downloading logs:', error);
    }
};

export const isMinorVersionUpgrade = (softwareStatus: SoftwareStatus, dcs: BaseComponent[]) => {
    try {
        const newestDcVersion = dcs.map((dc) => removeTrailingTags(dc.version)).sort((a, b) => b.localeCompare(a))[0];

        const inProgressVersion = removeTrailingTags(
            softwareStatus.versionUpgradeInProgress || softwareStatus.maxTowerVersion
        );

        if (!validate(newestDcVersion)) {
            return false;
        }

        if (!validate(inProgressVersion)) {
            return false;
        }

        const isSameMinorVersion =
            satisfies(inProgressVersion, `~${newestDcVersion}`) || satisfies(newestDcVersion, `~${inProgressVersion}`); // (major.minor.patch) Checks if castle and tagged upgrade version have same major and minor version

        return isSameMinorVersion;
    } catch (error) {
        console.error(
            `Error checking if minor version upgrade, castle version: ${softwareStatus.currentCastleVersion}, in progress version: ${softwareStatus.versionUpgradeInProgress}, max version: ${softwareStatus.maxTowerVersion}`,
            error
        );
        return false;
    }
};
